import { Helpers } from '@utils/helpers';
import { get, isEmpty, isNil } from 'lodash';

export class AboutYouForm {
  duties: string;
  nature: string;
  otherBusiness: string | undefined;
  fund: string[];
  otherFund: string | undefined;
  wealth: string[];
  otherWealth: string | undefined;
  income: number;

  constructor(initial: any) {
    this.duties = get(initial, 'duties', undefined);
    this.nature = get(initial, 'nature', undefined);
    this.otherBusiness = get(initial, 'otherBusiness', undefined);
    this.fund = get(initial, 'fund', undefined);
    this.otherFund = get(initial, 'otherFund', undefined);
    this.wealth = get(initial, 'wealth', undefined);
    this.otherWealth = get(initial, 'otherWealth', undefined);
    this.income = get(initial, 'income', undefined);
  }

  get reviewIncome(): { text: string, value: any }[] {
    const nature = this.nature === '12' ? this.otherBusiness : Helpers.getNatureByCode(this.nature);
    return [
      { text: 'Exact duties of occupation', value: this.duties },
      { text: "Nature of employer's business", value: nature || '' },
      this.sourceOfFunds,
      this.sourceOfWealths,
      { text: "Annual income", value: Helpers.toCurrency(Number(this.income)) },

    ]
  }

  get sourceOfFunds(): { text: string, value: any } {
    const funds = this.fund || [];
    return {
      text: 'Please select your source of funds <br/> (Origin of the funds used to pay your premiums)',
      value: funds.map(item => {
        if(item === 'other') {
          return this.otherFund;
        }
        return Helpers.getSourceByCode(item, 'fund');
      })
    }
  } 
  get sourceOfWealths(): { text: string, value: any } {
    const wealth = this.wealth || [];
    return {
      text: 'Please select your source of wealth <br/>(Where your wealth is derived from)',
      value: wealth.map(item => {
        if(item === 'other') {
          return this.otherWealth;
        }
        return Helpers.getSourceByCode(item, 'wealth');
      })
    }
  } 
}

export class AboutYouHealthForm {
  isSmoker: boolean | undefined;
  isLessThan40: string | undefined;
  isAppliedCI: boolean | undefined;
  height: number | undefined;
  weight: number | undefined;
  q1: string | undefined;
  q2: string | undefined;
  q3: string | undefined;
  q4: string | undefined;
  q5: string | undefined;
  public _healthType: string | undefined;
  public _hasCI: boolean = false;
  constructor(initial: any) {
    this.isSmoker = get(initial, 'isSmoker', false);
    this.isLessThan40 = get(initial, 'isLessThan40', undefined);
    this.isAppliedCI = get(initial, 'isAppliedCI', false);
    this.height = get(initial, 'height', undefined);
    this.weight = get(initial, 'weight', undefined);
    this.q1 = get(initial, 'q1', undefined);
    this.q2 = get(initial, 'q2', undefined);
    this.q3 = get(initial, 'q3', undefined);
    this.q4 = get(initial, 'q4', undefined);
    this.q5 = get(initial, 'q5', undefined);
  }

  set healthType(value: string) {
    this._healthType = value;
  }

  set hasCI(value: boolean) {
    this._hasCI = value;
  }

  get reviewHealth(): { text: string, children?: any[], value: any }[] {
    switch(this._healthType) {
      case 'SIOA3':
        return [
          {
            text: 'I declare the following',
            children: [
              'I do not have and have not been advised to seek medical treatment for any health conditions (other than for minor ailments) including cancer, tumour/lump, diabetes, hypertension, HIV/AIDS, heart disease, stroke/transient ischemic attack, any blood disorder, drug and/or alcohol habits. I am not currently seeking, receiving nor do I intend to seek or receive any advice/treatment.',
              'For the past 5 years, I have not been admitted to any hospital nor have I undergone any medical investigations or tests which were abnormal (other than for minor ailments).',
              'I do not engage or intend to engage in any hazardous activities for leisure or for work.',
              this._hasCI && 'I confirm that none of my natural parents, sisters or brothers has been diagnosed with or died from either breast, ovarian and/or colon cancer before attaining the age of 65.',
              'Where my total* life insurance coverage exceeds S$1 million, I have calculated that the total* is within 20 times of my current annual income.',
            ],
            value: 'Yes'
          }
        ].filter(o => !isEmpty(o) && !isNil(o));
      case 'SIOA1':
        return [
          {
            text: 'I declare the following',
            children: [
              'I have not been referred to or consulting any specialist medical practitioner (other than pregnancy related), or waiting for the results of any test or investigations.',
              this._hasCI && 'I confirm that none of my natural parents, sisters or brothers has been diagnosed with or died from either breast, ovarian and/or colon cancer before attaining the age of 65.',
              'Where my total* life insurance coverage exceeds S$1 million, I have calculated that the total* is within 20 times of my current annual income.'
            ],
            value: 'Yes'
          }
        ].filter(o => !isEmpty(o) && !isNil(o));
      case 'SIOB':
      default:
        const smorkingStatus = !this.isSmoker ? null : {
          text: 'You have declared that you are a smoker, how many sticks do you smoke daily?',
          value: this.isLessThan40 ? 'Less than 40 sticks' : '40 sticks and above'
        }
  
        const list: any = [
      
          {
            text: 'Have you had:',
            children: [
              'cancer or carcinoma in situ',
              'chest pain, heart attack or coronary heart disease',
              'stroke/ transient ischaemic attack',
              'diabetes',
              'chronic kidney disease',
              'arthritis'
            ],
            value: this.getValue(this.q1),
            id: 1
          },
          {
            text: 'In the past 5 years, have you had:',
            children: [
              'blood disorder',
              'mitral valve prolapse',
              'hepatitis B',
              'high blood pressure',
              'raised cholesterol',
              'thyroid disorder'
            ],
            value: this.getValue(this.q2),
            id: 2
          },
          {
            text: 'Have you had any health conditions which led up to:',
            children: [
              'more than 10 consecutive days off work, or',
              'follow-up consultations lasting a month or more, or',
              'treatment for 1 month or more?',
            ],
            value: this.getValue(this.q3),
            id: 3
          },
          {
            text: 'Have you:',
            children: [
              'had any abnormal medical investigations, tests or scans, or',
              'been awaiting for any pending medical investigations, tests or scans, or',
              'had any symptoms for which you intend to or have been advised to seek medical consultation or investigation?',
            ],
            value: this.getValue(this.q4),
            id: 4
          },
        ];
  
        if (this.isAppliedCI) {
          list.push({
            text: 'Have any of your natural parents, sisters or brothers died from or had breast, ovarian and/or colon cancer before the age of 65?',
            value: this.getValue(this.q4),
            children: undefined
          })
        }
  
        if(smorkingStatus) {
          return [ smorkingStatus, ...list ];
        }
        return list;
    }
  }

  private getValue(is: string | undefined): string {
    return is === 'Y'  ? 'Yes' : 'No';
  }


}